import { ReactElement } from 'react';

import ReactDOMServer from 'react-dom/server';

interface IRenderIconBadgeDataUri {
  icon: ReactElement;
  backgroundColor: string;
  iconColor?: string;
  size?: number;
}

const DEFAULT_SIZE = 96;
const DEFAULT_ICON_COLOR = '#ffffff';
const ICON_SCALE = 0.55; // ícone ocupa 55% do diâmetro do círculo

// Renderiza um ícone do @ant-design/icons como um "badge": círculo colorido
// de fundo + ícone por cima, tudo em um único PNG (data URI), pra poder ser
// embutido direto num <img> no PDF gerado pelo backend.
export async function renderIconBadgeDataUri({
  icon,
  backgroundColor,
  iconColor = DEFAULT_ICON_COLOR,
  size = DEFAULT_SIZE,
}: IRenderIconBadgeDataUri): Promise<string> {
  const iconMarkup = ReactDOMServer.renderToStaticMarkup(icon);
  const iconSvgEl = new DOMParser()
    .parseFromString(iconMarkup, 'text/html')
    .querySelector('svg');

  if (!iconSvgEl) {
    throw new Error(
      '[renderIconBadgeDataUri] Não foi possível extrair o SVG do ícone.',
    );
  }

  const iconSize = size * ICON_SCALE;
  const iconInset = (size - iconSize) / 2;

  iconSvgEl.setAttribute('width', String(iconSize));
  iconSvgEl.setAttribute('height', String(iconSize));
  iconSvgEl.setAttribute('x', String(iconInset));
  iconSvgEl.setAttribute('y', String(iconInset));
  iconSvgEl.setAttribute('fill', iconColor); // os <path> internos herdam esse fill

  const badgeSvg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
    `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="${backgroundColor}" />` +
    `${iconSvgEl.outerHTML}` +
    '</svg>';

  const svgDataUri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(badgeSvg)}`;

  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(
          new Error(
            '[renderIconBadgeDataUri] Canvas 2D context indisponível.',
          ),
        );
        return;
      }

      ctx.drawImage(image, 0, 0, size, size);
      resolve(canvas.toDataURL('image/png'));
    };

    image.onerror = () => {
      reject(
        new Error('[renderIconBadgeDataUri] Falha ao carregar o SVG do ícone.'),
      );
    };

    image.src = svgDataUri;
  });
}
